import { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
  Button,
} from '@mui/material';
import { Link, useHistory } from "react-router-dom";
import { StyledTableRowBinary } from 'src/shared/Styles/Styles';
// import { useStoreContext } from 'src/store/context';
// import { useAddAlert } from 'src/store/actions';
// import localforage from 'localforage';
// import { alertMessages } from 'src/constants';
import { ROUTE_PATHS } from 'src/routes';
import CustomTablePagination from 'src/shared/TablePagination/CustomTablePagination';
import { visuallyHidden } from '@mui/utils';
import { alertMessages, FuelModels } from 'src/constants';
import { useStoreContext } from 'src/store/context';
import { getFuelCatalogs } from 'src/apis/getCatalogCalls';
import { useAddAlert } from 'src/store/actions';
import LoadingSpinner from 'src/shared/loading/loading';
// import { errorResponse, validateCharacters, ValidCharacters } from 'src/store/common';

interface Data {
  catalogId: string
  fire_danger_model_name: string
  fuel_model: string
  mxd: number
  scm: number
  parent_catalog_name: string
  grass_type: string
  slope_class: string
  droughtCatalog: any
  deadFuelCatalog:any
  liveFuelCatalog:any
}

/***
 * Following is used as an Enhanced header to filter the headers as needed
 * 
 */
interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'fire_danger_model_name',
    numeric: false,
    disablePadding: false,
    label: 'CATALOG NAME',
  },
  {
    id: 'fuel_model',
    numeric: true,
    disablePadding: false,
    label: 'FUEL MODEL',
  },
  {
    id: 'mxd',
    numeric: true,
    disablePadding: false,
    label: 'MXD OVERRIDE',
  },
  {
    id: 'scm',
    numeric: true,
    disablePadding: false,
    label: 'SCM',
  },
  {
    id: 'grass_type',
    numeric: false,
    disablePadding: false,
    label: 'GRASS TYPE',
  },
  {
    id: 'slope_class',
    numeric: true,
    disablePadding: false,
    label: 'SLOPE CLASS',
  },
];

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
}
/***
 * Used for styling the Cells for the inner tables
 * 
 */
function EnhancedTableHead(props: Readonly <EnhancedTableProps>) {
  const { order, orderBy, onRequestSort}=
    props;

    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          (
            <TableCell
              className='headerParent'
              style={index ===0? {backgroundColor: '#d9d9d9', width: '30%'}: index===3?{backgroundColor: '#d9d9d9', width: '20%'}: {backgroundColor: '#d9d9d9', width: '10%'}}
              key={headCell.id}
              tabIndex={0}
              align={'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {/* Here we add the sort option + sort arrow */}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : <Box component="span" sx={visuallyHidden}></Box>}

                </TableSortLabel>

              }
            </TableCell>
          )
        ))}
      </TableRow>
    </TableHead>
  );
}

function FuelModelCatalogTable() {

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Data>('fire_danger_model_name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [, dispatch] = useStoreContext();

  const [selectFuelModel, setSelectFuelModel ] =useState('All');
  const [searchOptions, setSearchOptions] = useState<any[]>([])
  const addAlert = useAddAlert(dispatch);

  const history = useHistory();
  const [fuelCatalogTable, setFuelCatalogTable] = useState<any[]>([]);
  const [loading,setLoading] = useState<boolean>(false)

  /** Make the api call for the fuel catalog table */
  const fuelCatalogCallback = useCallback((request: any, search?:boolean) => {
    (async () => {
      try {
        setLoading(true);
        const fuelCatalogTable: any = await getFuelCatalogs(request, addAlert);
        if (fuelCatalogTable.data.length > 0) {
          if(search){
            setSearchOptions(fuelCatalogTable.data)
          }
          else{
            setFuelCatalogTable(fuelCatalogTable.data);
            setTotalCount(fuelCatalogTable._metadata.total_count);
          }
        } else {
          setFuelCatalogTable([]);
          setTotalCount(0);
        }
        setLoading(false);

      } catch (error) {
        setLoading(false);
        setFuelCatalogTable([]);
        addAlert({
          id: '',
          message: alertMessages.retrieveCatalog,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  }, [addAlert]);


  /*call for fuel catalog table*/
  useEffect(() => {
    const request = {
      id: null,
      catalogName: null,
      perPage: rowsPerPage,
      page: page,
      sortBy: orderBy,
      sortOrder: order,
      fuelModel: selectFuelModel.toUpperCase()
    }
    fuelCatalogCallback(request);
  }, [fuelCatalogCallback, order, orderBy, page, rowsPerPage, selectFuelModel]);

  useEffect(()=>{
    //for search
    const requestSearch = {
      id: null,
      catalogName: null,
      perPage: 10000,
      page: 0,
      sortBy: 'fire_danger_model_name',
      sortOrder: 'asc',
      fuelModel: 'ALL'
    }
    fuelCatalogCallback(requestSearch, true);
  },[fuelCatalogCallback])

  const onFuelCatalogSearch = (e:React.SyntheticEvent<Element, Event>, incomingVal: any) =>{
    history.push(
      { pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/detail/${incomingVal?.catalogId}`, state: {...incomingVal, from: 'search'} }
    )
    
  }

  const handleFuelModelChange = (newValue: any) =>{
    setSelectFuelModel(newValue)
    const fuelModelChangeRequest ={
      id: null,
      catalogName: null,
      perPage: rowsPerPage,
      page: page,
      sortBy: orderBy,
      sortOrder: order,
      fuelModel: newValue.toUpperCase()
    }
    setSelectFuelModel(newValue)
    fuelCatalogCallback(fuelModelChangeRequest);

  }
  const handleRequestSort = (event:React.MouseEvent<unknown>, property:keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0)
    const requestSortUpdate={
      perPage: rowsPerPage,
      page: 0,
      sortBy: property,
      sortOrder: isAsc ? 'desc' : 'asc',
      fuelModel: selectFuelModel.toUpperCase()
    }
    fuelCatalogCallback(requestSortUpdate);
  };


  /** Populate the drop downs for filtering the station table */

  const getLinkToCatalog = (catalog:any) => {
    return { pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/detail/${catalog?.catalogId}`, state: {...catalog} }
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    const updatePageCall = {
      perPage: rowsPerPage,
      page: newPage,
      sortBy: orderBy,
      sortOrder: order,
      fuelModel: selectFuelModel.toUpperCase()
    };
    fuelCatalogCallback(updatePageCall);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
    const updatePerPage = {
      perPage: Number.parseInt(event.target.value, 10),
      page: 0,
      sortBy: orderBy,
      sortOrder: order,
      fuelModel: selectFuelModel.toUpperCase()
    };
    fuelCatalogCallback(updatePerPage);
  };

  return (
    <div style={{overflowX: 'hidden', marginLeft: '.5rem', width: '95%'}}>
      {(loading) && (

        <div style={{ height: '30%' }}>
          <LoadingSpinner />
        </div>
      )}
      {/* Fuel Model Filters Section */}
      <Box sx={{ flexShrink: 0, mb: 2 }}>
        <div
          style={{
            marginLeft: '1.5%',
            paddingTop: '2%',
            maxWidth: '90vw',
          }}
        >
          <div>
            <div className="font-sans-md text-bold" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              {/* Search Fuel Model Catalog */}
              <div style={{ width: '37%'}}>
                <label
                  htmlFor="station"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  Search Fuel Model Catalogs
                </label>
                <div
                  className="searchStationsDownloadPage"
                >
                  {/* Search Icons */}
                  <i
                    className="fas fa-search"
                  />
                  <Autocomplete
                    id="fuelModelCatalogSearch"
                    options={searchOptions}
                    getOptionLabel={(option)=>`${option.fireDangerModelName}`}

                    onChange={(event, newValue) => onFuelCatalogSearch(event, newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        aria-label={'Search Fuel Model Catalogs...'}
                        placeholder="Search Fuel Model Catalogs..."
                      />
                    )}
                  />
                </div>
              </div>

              {/* Fuel Model Filter */}
              <div style={{ width: '11%' }}>
                <label
                  htmlFor="FuelModelType"
                  className="text-bold" style={{ fontSize: '18px' }}
                >
                  Fuel Model
                </label>
                <Autocomplete
                  className='height-55'
                  disableClearable
                  id="FuelModelType"
                  options={FuelModels}
                  value={selectFuelModel}

                  onChange={(event, newValue) => {
                    handleFuelModelChange(newValue)
                  }}
                  renderInput={(params) => (
                    <TextField
                      data-testid={'FuelModelType'}
                      {...params}
                      placeholder="Select Fuel"
                      InputProps={{
                        ...params.InputProps,
                        type: 'search',
                      }} />
                  )}
                />
              </div>
              <div style={{ width: '20%', marginRight: 0, marginLeft: 'auto'}}>
              <Button
                variant='contained'
                onClick={()=>{history.push(
                  {
                    pathname: `${ROUTE_PATHS.fuelCreateUpdate}`
                  }
                )}}
              >
                Create New Catalog</Button>
              </div>
            </div>
          </div>

        </div>

      {/* Table Section */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', paddingTop: '1rem' }}>

        <TableContainer
          component={Paper}
          className='margin-top-1'
          style={{
            width: '100%',
            maxWidth:'100%',
          }}
        >
          <Table stickyHeader>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {fuelCatalogTable.map((row, index) => (
                <StyledTableRowBinary
                  key={`${row?.fireDangerModelName}-${index}`}
                  hover
                  style={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    borderRadius: '4px',
                  }}
                >
                  <TableCell className='text-align-override'>
                    <Link
                      to={getLinkToCatalog(row)}
                      color="primary"
                      style={{ fontWeight: 500 }}
                    >
                      {row?.fireDangerModelName}
                    </Link>
                  </TableCell>
                  <TableCell className='text-align-override'>{row?.fuelModel}</TableCell>
                  <TableCell className='text-align-override'>{row?.mxdOverride}</TableCell>
                  <TableCell className='text-align-override'>{row?.scm}</TableCell>
                  <TableCell className='text-align-override'>{row?.grassType}</TableCell>
                  <TableCell className='text-align-override'>{row?.slopeClass}</TableCell>

                </StyledTableRowBinary>
              ))}
            </TableBody>
          </Table>
          <CustomTablePagination
            rowsPerPageOptions={[10, 20, 30]}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />   
        </TableContainer>
      </Box>
      </Box>
    </div>
  );
}

export default FuelModelCatalogTable;